/**
 * 投放计划内的 Web 直达链接管理，复用详情页表格与表单原子类（ADM-AP-S005）。
 * 候选按 WEB 与语言成员过滤；最终目标和环境 origin 由服务端校验。
 */
import {
  type AdPlanWebLink,
  type UpdateAdPlanWebLinkRequest,
  WEB_LINK_UTM_FIELDS,
} from '@novel-admin/shared';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useAdPlanWebLinks, useApps } from '~/data/queries';
import { ConfirmDialog } from '~/ui/Dialog';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

export function AdPlanWebLinksPanel({ planId, locale }: { planId: string; locale: string | null }) {
  const { query, create, update, remove } = useAdPlanWebLinks(planId);
  const apps = useApps();
  const [appId, setAppId] = useState('');
  const [editing, setEditing] = useState<AdPlanWebLink | null>(null);
  const [draft, setDraft] = useState<UpdateAdPlanWebLinkRequest>({});
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<AdPlanWebLink | null>(null);
  const candidates = apps.data.filter(
    (app) =>
      String(app.platform).toLowerCase() === 'web' &&
      (locale === 'en' || (locale !== null && app.lang.includes(locale))),
  );
  const pending = create.isPending || update.isPending || remove.isPending;
  const onError = (error: Error) =>
    toast(error instanceof AdminApiError ? error.message : 'Web 链接操作失败', { tone: 'neg' });
  function edit(link: AdPlanWebLink | null) {
    setEditing(link);
    setAppId(link?.app_id ?? '');
    setDraft(Object.fromEntries(WEB_LINK_UTM_FIELDS.map((key) => [key, link?.[key] ?? null])));
    setOpen(true);
  }
  // 以打开时的快照比较，只提交本次改动，避免覆盖其他运营同时编辑的 UTM。
  const editPatch: UpdateAdPlanWebLinkRequest = Object.fromEntries(
    WEB_LINK_UTM_FIELDS.filter((key) => (draft[key] ?? null) !== editing?.[key]).map((key) => [
      key,
      draft[key] ?? null,
    ]),
  );
  const success = () => {
    setOpen(false);
    toast('Web 链接已保存', { tone: 'pos' });
  };
  return (
    <section style={{ marginBottom: 18 }}>
      <div className="hstack" style={{ gap: 8, marginBottom: 8 }}>
        <strong>Web 投放链接</strong>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-sm"
          disabled={pending || apps.isLoading || apps.isError}
          onClick={() => edit(null)}
        >
          新建 Web 链接
        </button>
      </div>
      <p style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
        直达阅读器，无需 OneLink 或静态落地页。停用后仍可读书，但不作为新广告用户标识。
      </p>
      {query.isPending ? (
        <p>加载中…</p>
      ) : query.isError ? (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {query.error.message}
          <button type="button" className="btn btn-sm" onClick={() => void query.refetch()}>
            重试
          </button>
        </div>
      ) : query.data.items.length === 0 ? (
        <p style={{ color: 'var(--fg-muted)' }}>还没有 Web 投放链接</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>目标 App / URL</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((link) => (
              <tr key={link.id}>
                <td>
                  <div>{apps.data.find((app) => app.id === link.app_id)?.name ?? link.app_id}</div>
                  <a
                    className="mono"
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ overflowWrap: 'anywhere' }}
                  >
                    {link.url}
                  </a>
                </td>
                <td>{link.is_active ? '启用' : '停用'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={pending}
                    onClick={() => edit(link)}
                  >
                    编辑 UTM
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={pending}
                    onClick={() =>
                      update.mutate(
                        { linkId: link.id, body: { is_active: !link.is_active } },
                        { onError },
                      )
                    }
                  >
                    {link.is_active ? '停用' : '启用'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(link.url)
                        .then(() => toast('链接已复制', { tone: 'pos' }))
                        .catch(() => toast('复制失败，请手动复制链接', { tone: 'neg' }))
                    }
                  >
                    复制
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={pending}
                    onClick={() => setDeleting(link)}
                  >
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (editing ? Object.keys(editPatch).length === 0 : !appId) return;
            if (editing)
              update.mutate(
                { linkId: editing.id, body: editPatch },
                { onSuccess: success, onError },
              );
            else create.mutate({ app_id: appId, ...draft }, { onSuccess: success, onError });
          }}
          className="vstack"
          style={{ gap: 8, marginTop: 12 }}
        >
          {editing ? (
            <div>目标 App 与书编码创建后不可改 · {editing.book_code}</div>
          ) : (
            <label>
              目标 Web App
              <Select
                value={appId}
                disabled={pending}
                placeholder="选择 Web App"
                onChange={(event) => setAppId(event.target.value)}
              >
                {candidates.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {!editing && candidates.length === 0 && (
            <div role="status">没有符合本书语言的 Web App，请先在 App 管理创建。</div>
          )}
          {WEB_LINK_UTM_FIELDS.map((key) => (
            <label key={key}>
              {key}
              <input
                className="input mono"
                maxLength={256}
                value={draft[key] ?? ''}
                disabled={pending}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, [key]: event.target.value || null }))
                }
              />
            </label>
          ))}
          <div className="hstack" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-sm"
              disabled={pending}
              onClick={() => setOpen(false)}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-accent"
              disabled={pending || (editing ? Object.keys(editPatch).length === 0 : !appId)}
            >
              保存 Web 链接
            </button>
          </div>
        </form>
      )}
      {deleting && (
        <ConfirmDialog
          title="删除 Web 投放链接"
          message="删除后此链接不再通过新广告来源验证，既有账号来源保持。"
          tone="danger"
          confirmLabel="删除"
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            try {
              await remove.mutateAsync(deleting.id);
              setDeleting(null);
            } catch (error) {
              onError(error instanceof Error ? error : new Error('删除失败'));
            }
          }}
        />
      )}
    </section>
  );
}
